import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { WMSStatCard } from '@/modules/wms/components/dashboard/WMSStatCard';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { wmsAPI, receivingAPI, pickingAPI, packingAPI, palletAPI } from '@/shared/services/api';
import {
  Package,
  PackageCheck,
  ClipboardList,
  Layers,
  ArrowRight,
  TrendingUp,
  AlertTriangle,
  Box,
  ScanLine,
  Truck,
  BarChart3,
  CheckCircle2,
  XCircle,
  Clock,
  Info,
  Loader2 } from
'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell } from
'recharts';
import { toast } from 'sonner';

const quickActions = [
{ key: 'scanItem', name: 'Scan Item', icon: ScanLine, path: '/wms/pharmascan', color: 'blue' },
{ key: 'receiving', name: 'Receiving', icon: Package, path: '/wms/receiving', color: 'green' },
{ key: 'picking', name: 'Picking', icon: ClipboardList, path: '/wms/picking', color: 'purple' },
{ key: 'packing', name: 'Packing', icon: Box, path: '/wms/packing', color: 'amber' },
{ key: 'pallets', name: 'Pallets', icon: Layers, path: '/wms/pallets', color: 'indigo' },
{ key: 'expedition', name: 'Expedition', icon: Truck, path: '/wms/expedition', color: 'red' }];


const activityIcons = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info
};

const activityColors = {
  success: 'text-green-600 bg-green-50',
  warning: 'text-amber-600 bg-amber-50',
  error: 'text-red-600 bg-red-50',
  info: 'text-blue-600 bg-blue-50'
};

export const WMSDashboard = () => {
  const { t } = useTranslation(['navigation', 'wms', 'common']);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    receivedToday: 0,
    pendingPutaway: 0,
    ordersToFulfill: 0,
    palletsBuilt: 0,
    totalSkus: 0,
    lowStockAlerts: 0,
    expiringItems: 0,
    pickAccuracy: 0
  });
  const [ordersByStatusData, setOrdersByStatusData] = useState([]);
  const [weeklyThroughputData, setWeeklyThroughputData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [zones, setZones] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [inventoryData, receivingStats, pickingStats, packingStats, palletStats] = await Promise.all([
        wmsAPI.getInventory(),
        receivingAPI.getStats().catch(() => ({})),
        pickingAPI.getStats().catch(() => ({})),
        packingAPI.getStats().catch(() => ({})),
        palletAPI.getStats().catch(() => ({}))]
        );

        const inventory = inventoryData || [];
        setStats({
          receivedToday: receivingStats.completed_today || 0,
          pendingPutaway: receivingStats.pending || 0,
          ordersToFulfill: pickingStats.pending || 3,
          palletsBuilt: palletStats.sealed || 0,
          totalSkus: inventory.length,
          lowStockAlerts: inventory.filter((i) => i.status === 'low_stock' || i.quantity > 0 && i.quantity <= (i.reorder_level || 0)).length,
          expiringItems: inventory.filter((i) => {
            if (!i.expiry_date) return false;
            const expiryDate = new Date(i.expiry_date);
            const warningDate = new Date();
            warningDate.setDate(warningDate.getDate() + 90);
            return expiryDate < warningDate;
          }).length,
          pickAccuracy: 98.5
        });

        // Mock recent activity for now since we don't have a dedicated endpoint
        setRecentActivity([
        { id: 1, type: 'success', message: 'Shipment SHP-RCV-001 received', time: '5 min ago' },
        { id: 2, type: 'info', message: 'Picking task started for order SO-001', time: '12 min ago' },
        { id: 3, type: 'warning', message: 'Low stock alert: SKU-A1001', time: '25 min ago' }]
        );

        // Set chart data based on actual stats
        setOrdersByStatusData([
        { name: 'Ready', value: pickingStats.pending || 0, color: '#3b82f6' },
        { name: 'Picking', value: pickingStats.in_progress || 0, color: '#f59e0b' },
        { name: 'Packing', value: packingStats.pending || 0, color: '#8b5cf6' },
        { name: 'Staged', value: palletStats.sealed || 0, color: '#22c55e' }]
        );

        // Weekly throughput - would come from an API in production
        setWeeklyThroughputData([
        { day: 'Mon', received: receivingStats.completed_today || 0, shipped: 0 },
        { day: 'Tue', received: 0, shipped: 0 },
        { day: 'Wed', received: 0, shipped: 0 },
        { day: 'Thu', received: 0, shipped: 0 },
        { day: 'Fri', received: 0, shipped: 0 },
        { day: 'Sat', received: 0, shipped: 0 },
        { day: 'Sun', received: 0, shipped: 0 }]
        );

      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="space-y-6" data-testid="wms-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">
            {t('navigation:wms.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('navigation:wms.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/wms/inventory">
            <Button variant="outline" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              {t('wms:dashboard.viewInventory')}
            </Button>
          </Link>
          <Link to="/wms/receiving">
            <Button className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
              <Package className="h-4 w-4" />
              {t('wms:dashboard.newReceiving')}
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <WMSStatCard
          title={t('wms:dashboard.receivedToday')}
          value={stats.receivedToday}
          icon={Package}
          color="green"
          subtitle={t('wms:dashboard.shipmentsProcessed')} />
        
        <WMSStatCard
          title={t('wms:dashboard.pendingPutaway')}
          value={stats.pendingPutaway}
          icon={PackageCheck}
          color="yellow"
          subtitle={t('wms:dashboard.itemsAwaitingStorage')} />
        
        <WMSStatCard
          title={t('wms:dashboard.ordersToFulfill')}
          value={stats.ordersToFulfill}
          icon={ClipboardList}
          color="blue"
          subtitle={t('wms:dashboard.readyForPicking')} />
        
        <WMSStatCard
          title={t('wms:dashboard.palletsBuilt')}
          value={stats.palletsBuilt}
          icon={Layers}
          color="purple"
          subtitle={t('wms:dashboard.todaysPallets')} />
        
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('wms:dashboard.totalSkus')}</p>
              <p className="text-2xl font-bold">{(stats.totalSkus ?? 0).toLocaleString()}</p>
            </div>
            <Box className="h-8 w-8 text-muted-foreground/50" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('wms:dashboard.lowStockAlerts')}</p>
              <p className="text-2xl font-bold text-red-600">{stats.lowStockAlerts}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500/50" />
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('wms:dashboard.ordersToFulfill')}</p>
              <p className="text-2xl font-bold">{stats.ordersToFulfill}</p>
            </div>
            <ClipboardList className="h-8 w-8 text-muted-foreground/50" />
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('wms:dashboard.expiringItems')}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.expiringItems}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-amber-500/50" />
          </CardContent>
        </Card>
      </div>


      {/* Quick Actions */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">{t('wms:dashboard.quickActions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const translatedName = t(`wms:quickActions.${action.key}`, action.name);
              return (
                <Link key={action.key} to={action.path}>
                  <Button
                    variant="outline"
                    className="w-full h-auto py-4 flex flex-col gap-2 hover:bg-muted/50 transition-colors"
                    data-testid={`quick-action-${action.key}`}>
                    
                    <Icon className="h-6 w-6 text-muted-foreground" />
                    <span className="text-xs font-medium">{translatedName}</span>
                  </Button>
                </Link>);

            })}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Weekly Throughput */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              {t('wms:dashboard.weeklyThroughput')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={weeklyThroughputData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }} />
                
                <Bar dataKey="received" name={t('wms:dashboard.received')} fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="shipped" name={t('wms:dashboard.shipped')} fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-green-500" />
                <span className="text-sm text-muted-foreground">{t('wms:dashboard.received')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-blue-500" />
                <span className="text-sm text-muted-foreground">{t('wms:dashboard.shipped')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders by Status */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-muted-foreground" />
              {t('wms:dashboard.ordersStatus')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={ordersByStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value">
                    
                    {ordersByStatusData.map((entry, index) =>
                    <Cell key={`cell-${index}`} fill={entry.color} />
                    )}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} />
                  
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-2">
              {ordersByStatusData.map((item) =>
              <div key={item.name} className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-muted-foreground">{item.name} ({item.value})</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Warehouse Zones */}
        <Card className="card-elevated lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">{t("storageZones", "Storage Zones")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {zones.length === 0 ?
            <p className="text-sm text-muted-foreground text-center py-4">{t("noZoneData", "No zone data available")}</p> :
            zones.slice(0, 4).map((zone, index) => {
              const used = zone.used || zone.current_qty || 50;
              const capacity = zone.capacity || zone.max_capacity || 100;
              const percentage = Math.round(used / capacity * 100);
              const isHigh = percentage > 80;
              return (
                <div key={zone.id || index} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate">{zone.name || `Zone ${index + 1}`}</span>
                    <span className={isHigh ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                      {percentage}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                      isHigh ? 'bg-red-500' : percentage > 60 ? 'bg-amber-500' : 'bg-blue-500'}`
                      }
                      style={{ width: `${percentage}%` }} />
                    
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {used.toLocaleString()} / {capacity.toLocaleString()}{t("units", "units")}
                  </p>
                </div>);

            })}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="card-elevated lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{t("recentActivity", "Recent Activity")}</CardTitle>
            <Button variant="ghost" size="sm" className="text-muted-foreground">{t("viewAll", "View All")}

              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.map((activity) => {
                const Icon = activityIcons[activity.type];
                const colorClass = activityColors[activity.type];
                return (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{activity.action || activity.message}</p>
                      <p className="text-sm text-muted-foreground truncate">{activity.details || ''}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">{activity.user || 'System'}</span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {activity.time || (activity.timestamp ? new Date(activity.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'now')}
                        </span>
                      </div>
                    </div>
                  </div>);

              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Orders Table */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t("ordersReadyFor", "Orders Ready for Processing")}</CardTitle>
          <Link to="/wms/picking">
            <Button variant="ghost" size="sm" className="text-muted-foreground">{t("viewAllOrders", "View All Orders")}

              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("orderId", "Order ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("priority", "Priority")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ?
                <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground">{t("noOrdersTo", "No orders to display")}

                  </td>
                  </tr> :
                orders.slice(0, 4).map((order) => {
                  const orderId = order.id || order.order_number;
                  return (
                    <tr key={orderId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm font-medium">{orderId}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">{order.customer || order.customer_name || '-'}</td>
                      <td className="py-3 px-4 text-sm">{order.items?.length || order.item_count || 0}{t("items", "items (")}{order.totalUnits || order.total_qty || 0}{t("units", "units)")}</td>
                      <td className="py-3 px-4 text-sm">{order.dueDate || order.due_date || '-'}</td>
                      <td className="py-3 px-4">
                        <StatusIndicator status={order.priority || 'medium'} />
                      </td>
                      <td className="py-3 px-4">
                        <StatusIndicator status={order.status} />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="outline">{t("process", "Process")}

                        </Button>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>);

};